import { useState } from 'react';
import { Unlink as UnlinkIcon, Loader2, AlertTriangle } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { financeApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * UnlinkFromEconomicDialog
 *
 * Administrative recovery: sever the OwnTune ↔ e-conomic relationship
 * on an invoice WITHOUT touching e-conomic. The invoice returns to
 * "local_draft" (unlinked) and can be linked again to the correct
 * e-conomic invoice. Nothing in e-conomic is modified or deleted.
 *
 * Guardrails:
 *   * Two-click confirm (hard "Unlink invoice" red button after warning).
 *   * Optional free-text reason recorded in the finance_events audit row.
 *   * Displays which economic identifiers will be cleared before submit.
 */
export const UnlinkFromEconomicDialog = ({
    open,
    onOpenChange,
    orderId,
    invoice,
    onUnlinked,
}) => {
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const reset = () => {
        setReason('');
        setIsSubmitting(false);
    };

    const handleClose = (next) => {
        if (!next) reset();
        onOpenChange(next);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await financeApi.unlinkFromEconomic(orderId, reason.trim() || null);
            toast.success('Invoice unlinked from e-conomic');
            if (typeof onUnlinked === 'function') await onUnlinked();
            handleClose(false);
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to unlink invoice';
            toast.error(typeof detail === 'string' ? detail : 'Failed to unlink invoice');
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentDraft = invoice?.economic_draft_number || invoice?.economic_draft_invoice_number;
    const currentBooked = invoice?.economic_booked_invoice_number;
    const currentStatus = invoice?.economic_status;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md" data-testid="unlink-economic-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UnlinkIcon className="h-4 w-4 text-red-600" />
                        Unlink from e-conomic
                    </DialogTitle>
                    <DialogDescription>
                        Removes only the OwnTune ↔ e-conomic relationship. The
                        e-conomic invoice itself is <strong>not</strong> deleted or
                        modified. The OwnTune invoice returns to <em>unlinked</em>{' '}
                        so you can link it again.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div className="rounded border bg-slate-50 px-3 py-2 space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-slate-500">Current status</span>
                            <span className="font-mono">{currentStatus || '—'}</span>
                        </div>
                        {currentBooked ? (
                            <div className="flex justify-between">
                                <span className="text-slate-500">Booked invoice (will be cleared)</span>
                                <span className="font-mono">#{currentBooked}</span>
                            </div>
                        ) : null}
                        {currentDraft ? (
                            <div className="flex justify-between">
                                <span className="text-slate-500">Draft invoice (will be cleared)</span>
                                <span className="font-mono">#{currentDraft}</span>
                            </div>
                        ) : null}
                    </div>

                    <div className="flex gap-1.5 items-start text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                            Use this to fix a bad manual link. Cleared identifiers
                            are preserved on the record under
                            {' '}<span className="font-mono">economic_*_history</span>{' '}
                            for audit.
                        </span>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="unlink-reason" className="text-xs">
                            Reason (optional — recorded in audit log)
                        </Label>
                        <Textarea
                            id="unlink-reason"
                            data-testid="unlink-reason-input"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. Linked to wrong customer's invoice"
                            rows={2}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => handleClose(false)}
                        disabled={isSubmitting}
                        data-testid="unlink-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="unlink-submit"
                    >
                        {isSubmitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                        Unlink invoice
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UnlinkFromEconomicDialog;
